import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Link,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useConstants } from "../../Main/useConstants";
import { parseDateToLgComun3 } from "../../../../../../helpers/dateTransform";
import { IconHeader } from "../../../../../Icon/IconHader/IconHeader";
import {
  FolderAdd,
  OutlineDocumentText,
  OutlineEdit,
  OutlineEye,
  Printer,
  TrashCan,
} from "../../../../../Icon";
import { InterviewQnA } from "../InterviewQnA/InterviewQnA";
import { deleteInterview } from "../../../../../../apis";
import { ChipParticipant } from "../../components/InvestigationComponents";
import { EGIconButton } from "../../../../../MainComponents/Button/IconButton/IconButton";
import { EGActionSheet } from "../../../../../MainComponents/Button/Menu/ActionSheet/EGActionSheet";
import { AddConsentFiles } from "./AddConsentFiles/AddConsentFIles";
import { PrintInterview } from "./PrintInterview/PrintInterview";
import { PreviewFile } from "./PreviewFile/PreviewFile";
import { SectionBox } from "./SectionBox/SectionBox";
import { sanitizeForInnerHTML } from "../../../../../../helpers/sanitizeHtml";

export const InterviewGeneralInfo = ({
  id,
  interview,
  hdlEditInterview,
  setInterviews,
  alias,
  justView,
  openConfirmDeleteModal,
  closeModalConfirm,
  updateModalConfirm,
  expanded,
  onToggle,
  registerRef,
  onOpened,
  summaryMode = false,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  const {
    enums,
    interviews: {
      statusOpts,
      rolesOpts,
      typeFileOpts,
      pointsToEvaluateLabels,
      connectionProviderOpts,
    },
    functions: { getUserLabel, getUsersWitness },
  } = useConstants();

  const rootRef = useRef(null);
  // Open uoploader consent files
  const [showAddConsentFiles, setShowAddConsentAttachments] = useState(false);
  // Open preview attachment files
  const [selectPreviewFile, setSelectPreviewFile] = useState(null);
  // Open cuestions & answers
  const [openInterviewQnA, setOpenInterviewQnA] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);

  const [openPrintPreview, setOpenPrintPreview] = useState(false);

  // Interviewed type
  const interviewed = useMemo(() => {
    if (typeof interview.interviewed === "string") {
      return interview.interviewed;
    }
    if (interview.interviewed?.from === enums.typeUser.COMPLAINANT) {
      return t("Interview.the_complainant");
    }
    // use getUserLabel to resolve the label of the interviewee
    const label = getUserLabel(interview.interviewed);
    return label ?? t("Interview.unknown_user");
  }, [interview.interviewed, getUserLabel, enums.typeUser.COMPLAINANT, t]);

  const participants = useMemo(
    () => interview.witness ?? [],
    [interview.witness],
  );

  const qnaItems = interview.interview ?? [];

  const evaluationPoints = useMemo(() => {
    const points = interview?.evaluation?.points;
    if (!points) return [];
    return Object.entries(points)
      .map(([key, value]) => ({ key, value }))
      .filter((p) => p.value?.value && p.value.value !== "not-defined");
  }, [interview?.evaluation?.points]);

  // En summaryMode el Collapse muestra sólo participantes, objetivos y tipo
  // de entrevista; el resto se ve en el modal del ojo.
  const showRichSections = !summaryMode;
  // Delete interview
  const handleDeleteInterview = useCallback(async () => {
    try {
      const resp = await deleteInterview(alias, id);
      setInterviews(resp.data.interviews);
      closeModalConfirm();
    } catch (err) {
      console.error(err);
    }
  }, [alias, id, setInterviews, closeModalConfirm]);

  // Open delete confirmation modal
  const openModalDeleteInterview = useCallback(() => {
    openConfirmDeleteModal({
      onConfirm: async () => {
        updateModalConfirm({ loading: true });
        try {
          await handleDeleteInterview();
        } catch (err) {
          console.error(err);
        } finally {
          updateModalConfirm({ loading: false });
        }
      },
    });
  }, [openConfirmDeleteModal, updateModalConfirm, handleDeleteInterview]);

  // Menu actions
  const actions = useMemo(
    () =>
      [
        !justView && {
          name: t("Interview.attach_consent_documents"),
          icon: <FolderAdd color={theme.palette.success.main} />,
          onClick: () => setShowAddConsentAttachments(true),
        },
        {
          name: t("Interview.questions_and_answers"),
          icon: <OutlineDocumentText color={theme.palette.warning.dark} />,
          onClick: () => setOpenInterviewQnA(true),
        },
        {
          name: t("causes.edit"),
          icon: <OutlineEdit color={theme.palette.primary.main} />,
          onClick: () => hdlEditInterview(interview, id),
          disabled: justView,
        },

        {
          name: t("causes.print"),
          icon: <Printer color={theme.palette.blueGray700.main} />,
          onClick: () => setOpenPrintPreview(true),
        },
        {
          name: t("causes.delete"),
          icon: <TrashCan color={theme.palette.error.main} />,
          textColor: "error",
          onClick: openModalDeleteInterview,
          disabled: justView,
        },
      ].filter(Boolean),
    [
      theme,
      t,
      justView,
      interview,
      id,
      setOpenInterviewQnA,
      hdlEditInterview,
      openModalDeleteInterview,
    ],
  );

  // Current status label
  const currentStatus = statusOpts.find(
    (opt) => opt.value === interview.status,
  );
  // Color based on status
  const borderColor = currentStatus
    ? typeof currentStatus.color === "function"
      ? currentStatus.color(theme)
      : currentStatus.color
    : (theme) => theme.palette.secondary.main;

  const typeKeyMap = {
    "in-person": "Interview.in_person",
    "video-conference": "Interview.via_video_conference",
  };

  const typeLabel = t(typeKeyMap[interview.kind] ?? "");

  // Turn a link into a hyperlink
  const renderKindDetails = (text) => {
    if (!text) return null;

    const urlRegex =
      /((https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*))/gi;

    const elements = [];
    let lastIndex = 0;
    let keyCounter = 0;

    for (const match of text.matchAll(urlRegex)) {
      const url = match[0];
      const index = match.index ?? 0;

      if (index > lastIndex) {
        elements.push(text.slice(lastIndex, index));
      }

      // normalice href
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;

      elements.push(
        <Link
          key={`link-${keyCounter++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ wordBreak: "break-word" }}
        >
          {url}
        </Link>,
      );

      lastIndex = index + url.length;
    }

    if (lastIndex < text.length) {
      elements.push(text.slice(lastIndex));
    }

    return elements.length ? elements : text;
  };

  const providerLabel =
    connectionProviderOpts.find(
      (opt) => opt.value === interview?.connectionProvider,
    )?.label || "";

  const handleOpenPreview = (data) => {
    setOpenPreview(true);
    setSelectPreviewFile(data);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectPreviewFile(null);
  };

  const attachmentsPurpose = interview.attachments.filter(
    (item) =>
      item.attachments_data?.purpose &&
      item.attachments_data.purpose !== "other",
  );
  const purposeLabel = (value) =>
    typeFileOpts.find((opt) => opt.value === value)?.label || "";

  return (
    <>
      <AddConsentFiles
        id={id}
        show={showAddConsentFiles}
        alias={alias}
        onClose={() => setShowAddConsentAttachments(false)}
        attachments={interview.attachments || []}
        setInterviews={setInterviews}
        typeFileOpts={typeFileOpts}
      />
      {/* Consent file preview modal */}
      <PreviewFile
        show={openPreview}
        theme={theme}
        t={t}
        selectPreviewFile={selectPreviewFile}
        onClose={handleClosePreview}
      />
      <InterviewQnA
        id={id}
        alias={alias}
        show={openInterviewQnA}
        onClose={() => setOpenInterviewQnA(false)}
        setShow={setOpenInterviewQnA}
        interview={interview || []}
        setInterviews={setInterviews}
        summaryMode={summaryMode}
      />
      <PrintInterview
        interview={interview}
        show={openPrintPreview}
        onClose={() => setOpenPrintPreview(false)}
        t={t}
        theme={theme}
        getUserLabel={getUserLabel}
        getUsersWitness={getUsersWitness}
        rolesOpts={rolesOpts}
        typeLabel={typeLabel}
        pointsToEvaluateLabels={pointsToEvaluateLabels}
        providerLabel={providerLabel}
        interviewed={interviewed}
      />

      <Card
        ref={(el) => {
          rootRef.current = el;
          registerRef?.(interview.id, el);
        }}
        sx={{
          alignSelf: "start",
          boxShadow: expanded
            ? `0 0 0 2px ${alpha(
                theme.palette.primary.main,
                0.5,
              )}, 0 6px 18px ${alpha(theme.palette.primary.main, 0.22)}`
            : `0 0 5px -1px ${theme.palette.blueGray700.main}`,
          transition: theme.transitions.create(["box-shadow"], {
            duration: 200,
          }),
        }}
      >
        {/* Title & actions */}
        <Box
          sx={{
            py: 0.33,
            borderBottom: `1px solid ${
              expanded
                ? alpha(theme.palette.primary.main, 0.15)
                : theme.palette.blueGray50.main
            }`,
            backgroundColor: expanded
              ? alpha(theme.palette.primary.main, 0.08)
              : theme.palette.blueGray50.main,
            transition: theme.transitions.create(
              ["background-color", "border-color"],
              { duration: 200 },
            ),
          }}
        >
          <CardHeader
            sx={{ p: 1 }}
            title={
              <Typography variant="h5" px={0.5} mr={3}>
                {`${t("Interview.interview_with")} ${interviewed}`}
              </Typography>
            }
            action={
              <>
                {!summaryMode && (
                  <EGActionSheet
                    theme={theme}
                    actions={actions}
                    renderTrigger={(openMenu) => (
                      <EGIconButton
                        iconName="more_dots3"
                        iconSize={20}
                        iconColor="primary"
                        onClick={openMenu}
                        title={t("investigation.actions")}
                      />
                    )}
                  />
                )}
                {summaryMode && (
                  <EGIconButton
                    iconName="outline_eye"
                    iconColor="primary"
                    iconSize={20}
                    title={t("Interview.preview")}
                    onClick={() => setOpenInterviewQnA(true)}
                  />
                )}
                <EGIconButton
                  title={
                    expanded
                      ? t("Interview.close_details")
                      : t("Interview.show_details")
                  }
                  iconSize={20}
                  iconColor="primary"
                  iconName={expanded ? "close_icon" : "arrow_down_2"}
                  sx={{
                    transform: !expanded ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: theme.transitions.create("transform", {
                      duration: 200,
                    }),
                  }}
                  onClick={onToggle}
                  aria-expanded={expanded ? "true" : "false"}
                  aria-label={expanded ? "collapse" : "expand"}
                />
              </>
            }
          />
        </Box>

        <CardContent sx={{ p: 1, position: "relative" }}>
          {/* Scheduled date */}
          <Typography fontSize={12.5} textAlign="center" mb={1}>
            <Box component="span" color="blueGray900" fontWeight={600}>
              {t("Interview.interview_date")}:
            </Box>
            <Box
              component="span"
              display="block"
              color="primary.main"
              fontWeight={700}
            >
              {interview.scheduled
                ? parseDateToLgComun3(interview.scheduled, undefined, undefined, false)
                : t("Interview.unspecified")}
            </Box>
          </Typography>
          {/* Interviewer & status */}
          <Box
            sx={{
              display: "grid",
              gap: 1,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "2fr 3fr",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                border: `1px solid ${borderColor}`,
                borderLeft: `6px solid ${borderColor}`,
                backgroundColor: alpha(borderColor, 0.05),
                minHeight: 10,
                py: 0.75,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Typography fontSize={12.5} fontWeight={600} color="blueGray900">
                {`${t("Interview.status")}: ${
                  currentStatus?.label ?? t("Interview.unspecified")
                }`}
              </Typography>
            </Box>

            <Typography fontSize={12.5} textAlign="center" my="auto">
              <Box component="span" color="blueGray900" fontWeight={600}>
                {t("Interview.interviewer")}:
              </Box>
              <Box
                component="span"
                display="block"
                fontWeight={600}
                sx={{
                  textDecoration: "underline",
                  textDecorationColor: theme.palette.secondary.main,
                  textUnderlineOffset: "3px",
                }}
              >
                {getUserLabel(interview.interviewer)}
              </Box>
            </Typography>
          </Box>

          <Collapse
            in={!!expanded}
            timeout="auto"
            unmountOnExit
            onEntered={() => {
              onOpened?.(interview.id);
            }}
          >
            {/* Participants list */}
            {participants.length > 0 && (
              <Box mt={1}>
                <Typography
                  mb={0.5}
                  fontSize={12.5}
                  fontWeight={600}
                  color="blueGray900"
                >
                  {`${t("Interview.participant")}${
                    participants.length > 0 ? "s" : ""
                  }: `}
                </Typography>
                <Stack
                  spacing={1}
                  direction={"row"}
                  flexWrap={"wrap"}
                  gap={0.5}
                  px={0.5}
                >
                  {participants.map((participant, idx) => (
                    <ChipParticipant
                      key={idx}
                      role={
                        rolesOpts.find((opt) => opt.value === participant.role)
                          ?.label
                      }
                      name={getUserLabel(participant)}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            {/* Objectives */}
            {interview.details && (
              <SectionBox title={`${t("Interview.interview_objectives")}:`}>
                <Typography
                  variant="body1"
                  textAlign="justify"
                  sx={{ textIndent: "1.5em" }}
                >
                  {interview.details}
                </Typography>
              </SectionBox>
            )}
            {/* Details about the type of interview */}
            <SectionBox
              title={
                <>
                  {t("Interview.interview")} {typeLabel}
                  {providerLabel && ` (${providerLabel})`}
                </>
              }
            >
              {!!interview.kindDetails && (
                <Typography
                  variant="body1"
                  textAlign="justify"
                  sx={{ overflowWrap: "anywhere", whiteSpace: "pre-line" }}
                >
                  {renderKindDetails(interview.kindDetails)}
                </Typography>
              )}
            </SectionBox>
            {showRichSections && qnaItems.length > 0 && (
              <SectionBox title={t("Interview.questions_and_answers")}>
                <Stack spacing={1.5}>
                  {qnaItems.map((item, index) => (
                    <Stack key={index} spacing={0.5}>
                      <Typography fontSize={13} fontWeight={600}>
                        {`${index + 1}. ${item.question}`}
                      </Typography>
                      {item?.details && (
                        <Typography
                          fontSize={13}
                          textAlign="justify"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {`${t("Interview.objective")}: ${item.details}`}
                        </Typography>
                      )}
                      {item?.answer ? (
                        <Typography
                          fontSize={13}
                          fontStyle="italic"
                          textAlign="justify"
                          sx={{
                            py: 0.75,
                            px: 1,
                            borderRadius: 2,
                            whiteSpace: "pre-wrap",
                            background: alpha(
                              theme.palette.blueGray50.main,
                              0.75,
                            ),
                          }}
                        >
                          — {item.answer}
                        </Typography>
                      ) : (
                        <Typography
                          fontSize={12}
                          sx={{
                            fontStyle: "italic",
                            color: "secondary.light",
                            px: 1,
                          }}
                        >
                          {t("Interview.no_response_recorded")}
                        </Typography>
                      )}
                      {item?.answerDetails?.trim() && (
                        <Typography
                          fontSize={13}
                          textAlign="justify"
                          sx={{
                            p: 0.5,
                            borderLeft: `3px solid ${theme.palette.blueGray200.main}`,
                            borderRadius: 1,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <Box component="span" fontWeight={600}>
                            {t("evaluation.additional_details")}:
                          </Box>{" "}
                          {item.answerDetails}
                        </Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </SectionBox>
            )}
            {showRichSections &&
              (evaluationPoints.length > 0 ||
                interview?.evaluation?.details?.trim()) && (
              <SectionBox title={t("Interview.interview_evaluation")}>
                {evaluationPoints.length > 0 && (
                  <Stack spacing={0.75}>
                    {evaluationPoints.map(({ key, value }) => (
                      <Box key={key}>
                        <Typography fontSize={13} fontWeight={600}>
                          {pointsToEvaluateLabels[key] || key}
                        </Typography>
                        <Typography
                          fontSize={13}
                          fontWeight={600}
                          color="text.secondary"
                          pl={1.5}
                        >
                          – {t(`evaluation.${value.value}`)}
                        </Typography>
                        {value?.details?.trim() && (
                          <Typography
                            fontSize={13}
                            textAlign="justify"
                            sx={{
                              mt: 0.25,
                              ml: 1.5,
                              p: 0.5,
                              borderLeft: `3px solid ${theme.palette.blueGray200.main}`,
                              borderRadius: 1,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            <Box component="span" fontWeight={600}>
                              {t("evaluation.specific_comment")}:
                            </Box>{" "}
                            {value.details}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
                {interview?.evaluation?.details?.trim() && (
                  <Typography
                    fontSize={13}
                    textAlign="justify"
                    mt={evaluationPoints.length > 0 ? 1 : 0}
                    sx={{ textIndent: "1.5em", whiteSpace: "pre-line" }}
                  >
                    <Box component="span" fontWeight={600}>
                      {t("evaluation.general_evaluation_comments")}:
                    </Box>{" "}
                    {interview.evaluation.details}
                  </Typography>
                )}
              </SectionBox>
            )}
            {showRichSections && interview?.conclusion && (
              <SectionBox title={t("Interview.conclusion")}>
                <Typography
                  component="div"
                  variant="body1"
                  sx={{ textAlign: "justify", "& p": { m: 0 } }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeForInnerHTML(interview.conclusion),
                  }}
                />
              </SectionBox>
            )}
            {showRichSections && attachmentsPurpose.length > 0 && (
              <Box mt={1}>
                <IconHeader
                  title={t("Interview.consent_files")}
                  uppercase={false}
                  iconName="folder_2"
                  variant="h6"
                />

                <Box
                  mt={0.5}
                  sx={{
                    display: "grid",
                    gap: 1,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    },
                  }}
                >
                  {attachmentsPurpose.map((item, idx) => (
                    <ListItem
                      key={idx}
                      secondaryAction={
                        <EGIconButton
                          iconName="outline_import"
                          title={t("Interview.download")}
                          href={item.file_url}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Download ${item.title}`}
                        />
                      }
                      disablePadding
                    >
                      <ListItemButton
                        role={undefined}
                        dense
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 4,
                          height: "100%",
                        }}
                        onClick={() => handleOpenPreview(item)}
                      >
                        <ListItemIcon
                          sx={{
                            border: `1px solid ${theme.palette.blueGray50.main}`,
                            borderRadius: "50%",
                            minWidth: "auto",
                            p: 0.5,
                            mr: 1,
                            bgcolor: theme.palette.background.paper,
                          }}
                        >
                          <OutlineDocumentText
                            color={theme.palette.primary.dark}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={purposeLabel(item.attachments_data.purpose)}
                          secondary={item.attachments_data.name}
                          primaryTypographyProps={{
                            textAlign: "center",
                            fontWeight: 600,
                          }}
                          secondaryTypographyProps={{
                            textAlign: "center",
                            fontSize: 12,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </Box>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>
    </>
  );
};
